import {type ReactNode} from 'react'
import {StyleSheet, Text, View} from 'react-native'

import {
  useFlashBookPlayDesign,
  useFlashBookPlayPalette,
  usePlayDirectionTextStyle,
} from '#/components/flashbooks/FlashBookPlayTheme'
import {THEME_HEADING_GAP} from '#/themes-flashbook/design'

/**
 * The two pieces every question widget needs to feel like it belongs to the
 * book rather than to a form: how the question announces itself, and what sits
 * at the head of an answer.
 *
 * Both read the active theme's design. Outside a book the design falls back to
 * the neutral default, which renders them the way capsule play has always
 * looked, so nothing else on the app changes.
 */

/** A–Z for lettered answer markers. */
export function optionLetter(index: number): string {
  return String.fromCharCode(65 + (index % 26))
}

/**
 * The question text, presented in the theme's voice.
 *
 * `eyebrow` puts a small uppercase kicker above it (classroom, editorial),
 * `tab` sets that kicker in an accent tab (mission briefing), and `heading`
 * gives the question a heavier setting with a rule beneath it (storybook).
 */
export function QuestionHeading({
  label,
  children,
  style,
}: {
  /** The block's short label, e.g. "Multiple choice". */
  label?: string
  children: ReactNode
  style?: object
}) {
  const d = useFlashBookPlayDesign()
  const p = useFlashBookPlayPalette()
  const kind = d.quiz.question
  // A ruled theme sets this to 0 so the kicker and the question land on
  // consecutive lines of the paper instead of straddling a gap.
  const gap = d.quiz.headingGap ?? THEME_HEADING_GAP

  /**
   * The line's alignment, in the direction this page is written.
   *
   * Every one of these is a bare `Text`, and React Native Web gives a `Text`
   * `dir="auto"` — so the browser picks each line's direction from its *own*
   * first strong character. That is right for content and wrong for a label:
   * the block's kicker is the app's own English word ("Single choice"), so on
   * an Arabic page it kept resolving left-to-right and sat against the left
   * margin while the question above it read from the right. A label belongs to
   * the page it is printed on, so it takes the page's alignment explicitly.
   *
   * Outside a book this is an empty object, so capsule play is unchanged.
   */
  const line = usePlayDirectionTextStyle()

  if (kind === 'plain' || !label) {
    return (
      <Text style={[d.type.question, line, {color: p.text}, style]}>
        {children}
      </Text>
    )
  }

  if (kind === 'tab') {
    return (
      <View style={[qz.group, {gap}]}>
        <View
          style={[
            qz.tab,
            {backgroundColor: p.accent + '1f', borderColor: p.accent + '55'},
          ]}>
          <Text style={[d.type.caption, line, {color: p.accent}]}>
            {label.toUpperCase()}
          </Text>
        </View>
        <Text style={[d.type.question, line, {color: p.text}, style]}>
          {children}
        </Text>
      </View>
    )
  }

  if (kind === 'heading') {
    return (
      <View style={[qz.group, {gap}]}>
        <Text style={[d.type.question, line, {color: p.text}, style]}>
          {children}
        </Text>
        <View
          style={[
            qz.underRule,
            {backgroundColor: p.accent, opacity: d.block.ruleOpacity},
          ]}
        />
      </View>
    )
  }

  // eyebrow
  return (
    <View style={[qz.group, {gap}]}>
      <Text style={[d.type.caption, line, {color: p.muted}]}>
        {label.toUpperCase()}
      </Text>
      <Text style={[d.type.question, line, {color: p.text}, style]}>
        {children}
      </Text>
    </View>
  )
}

/**
 * The marker at the head of an answer row: a radio dot, a lettered chip, a
 * mission checkbox, a dash, or nothing at all.
 *
 * `tone` is the resolved state colour (accent when selected, success/error when
 * the answer has been judged) so the marker can never disagree with the row.
 */
export function OptionMarker({
  index,
  tone,
  selected,
}: {
  index: number
  tone: string
  selected: boolean
}) {
  const d = useFlashBookPlayDesign()
  const p = useFlashBookPlayPalette()

  switch (d.quiz.marker) {
    case 'none':
      return null
    case 'letter':
      return (
        <View
          style={[
            qz.letter,
            {
              borderColor: selected ? tone : p.muted,
              backgroundColor: selected ? tone + '22' : 'transparent',
            },
          ]}>
          <Text style={[qz.letterTxt, {color: selected ? tone : p.muted}]}>
            {optionLetter(index)}
          </Text>
        </View>
      )
    case 'box':
      return (
        <View
          style={[
            qz.box,
            {
              borderColor: selected ? tone : p.muted,
              backgroundColor: selected ? tone + '22' : 'transparent',
            },
          ]}>
          {selected && <View style={[qz.boxDot, {backgroundColor: tone}]} />}
        </View>
      )
    case 'dash':
      return (
        <View style={qz.dash}>
          <View
            style={{
              width: selected ? 16 : 10,
              height: 2,
              borderRadius: 1,
              backgroundColor: selected ? tone : p.muted,
            }}
          />
        </View>
      )
    case 'radio':
    default:
      return (
        <View style={[qz.radio, {borderColor: selected ? tone : p.muted}]}>
          {selected && <View style={[qz.radioDot, {backgroundColor: tone}]} />}
        </View>
      )
  }
}

const qz = StyleSheet.create({
  /** Overridden per theme by `quiz.headingGap`. */
  group: {gap: THEME_HEADING_GAP},
  tab: {
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
    borderWidth: 1,
  },
  underRule: {height: 2, width: 34, borderRadius: 1, marginTop: 2},
  radio: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioDot: {width: 10, height: 10, borderRadius: 5},
  letter: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  letterTxt: {fontSize: 12, fontWeight: '800'},
  box: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  boxDot: {width: 9, height: 9, borderRadius: 2},
  dash: {width: 20, alignItems: 'center', justifyContent: 'center'},
})
